/**
 * The team-split columns: one per side of the pitch, each with a live
 * heat map and the passes played, flanking the video (Ben, 2026-09-03).
 *
 * Both panels follow the playhead at the page's one-second tick. The heat
 * map reuses the canvas layer's own binning (heatMap.accumulate — same
 * math, so the panel and the pitch overlay can never disagree); the pass
 * panel draws the completed passes derived from the pass-potential
 * moments, most recent brightest, arrowheads for direction.
 */
import { useEffect, useId, useRef, useState } from 'react'

import type { PassPotentialStatus } from './canvasController'
import { pathSeg } from '../../lib/urls'
import type { TrackingDataCache, TrackingFrame } from './trackingLoader'
import { HALF_LIFE_FRAMES, accumulate, renderGrid } from './heatMap'
import { TeamShapePanel, ZoneOccupancyPanel, useLiveFrame, type LiveFrameRef } from './shapePanels'

export { useLiveFrame }
export type { LiveFrameRef }
import { GRID_X_CELLS, GRID_Y_CELLS, computeControlGrid } from './spatialCompute'
import { describeChain, type Chain } from './chains'
import { dotContrast } from './dotContrast'
import {
  BOX_DEPTH_M, BOX_HALF_WIDTH_M, CHAIN_ZONES, aggregateNetwork, attackingSequences,
  buildUpProfile, passesIntoBox, type CompletedPass,
} from './teamSplit'

/** Sampled heat window: every 15th frame over the last minute of play.
 *  120 samples stays inside the cache's LRU, so a steady tick re-reads
 *  cached frames rather than re-converting the window every second. */
const HEAT_STEP = 15
const HEAT_SPAN = 1800

/** Internal canvas resolution: 4x the 106x69 heat grid. */
const CW = 424, CH = 276

/** How many of the most recent passes stay on the panel. */
const PASS_TRAIL = 24

/**
 * Crisp canvas at any display size: the panels draw in the fixed CW x CH
 * logical space, but the backing store is sized to the element's CSS width
 * x devicePixelRatio and the context transform maps logical -> device px.
 * Without this a 424px buffer stretched to a large column renders soft
 * dots and unreadable jersey numbers. Returns a counter that bumps on
 * resize so draw effects re-run when the element changes size.
 */
function useCanvasResizeEpoch(ref: React.RefObject<HTMLCanvasElement | null>): number {
  const [epoch, setEpoch] = useState(0)
  useEffect(() => {
    const cv = ref.current
    if (!cv || typeof ResizeObserver === 'undefined') return
    let lastW = 0
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width ?? 0
      if (Math.abs(w - lastW) > 1) { lastW = w; setEpoch((e) => e + 1) }
    })
    ro.observe(cv)
    return () => ro.disconnect()
  }, [ref])
  return epoch
}

/** Size the backing store from the displayed width (aspect held at CW:CH)
 *  and map the logical CW x CH space onto it. Call at the top of a draw.
 *  Returns the logical->device scale, for effects (ctx.filter blur) that
 *  the transform does not reach. */
function fitCanvas(cv: HTMLCanvasElement, ctx: CanvasRenderingContext2D): number {
  const dpr = window.devicePixelRatio || 1
  const cssW = cv.getBoundingClientRect().width || CW
  const bw = Math.max(1, Math.round(cssW * dpr))
  const bh = Math.max(1, Math.round(bw * (CH / CW)))
  if (cv.width !== bw || cv.height !== bh) { cv.width = bw; cv.height = bh }
  ctx.setTransform(bw / CW, 0, 0, bh / CH, 0, 0)
  return bw / CW
}

function drawPitchLines(ctx: CanvasRenderingContext2D) {
  ctx.strokeStyle = 'rgba(255,255,255,0.35)'
  ctx.lineWidth = 1
  const w = CW, h = CH, mx = w / 2, my = h / 2
  const sx = w / 105, sy = h / 68
  ctx.strokeRect(0.5, 0.5, w - 1, h - 1)
  ctx.beginPath(); ctx.moveTo(mx, 0); ctx.lineTo(mx, h); ctx.stroke()
  ctx.beginPath(); ctx.arc(mx, my, 9.15 * sx, 0, Math.PI * 2); ctx.stroke()
  const boxW = 16.5 * sx, boxH = 40.32 * sy
  ctx.strokeRect(0.5, my - boxH / 2, boxW, boxH)
  ctx.strokeRect(w - boxW - 0.5, my - boxH / 2, boxW, boxH)
}

export function TeamHeatPanel({ cache, frame, team, color, kickoffFrame }: {
  cache: TrackingDataCache | null
  frame: number
  team: number
  color: string
  /** First frame of actual play. 0 for legacy sync (tracking is renumbered
   *  kickoff=0), but v3 frame-aligned games number pre-kickoff video too,
   *  and warmup positions are not heat (Ben, 2026-09-03). */
  kickoffFrame: number
}) {
  const ref = useRef<HTMLCanvasElement>(null)
  const resizeEpoch = useCanvasResizeEpoch(ref)

  useEffect(() => {
    const cv = ref.current
    if (!cv || !cache) return
    const samples: TrackingFrame[] = []
    for (let f = Math.max(kickoffFrame, frame - HEAT_SPAN); f <= frame; f += HEAT_STEP) {
      const d = cache.getFrameData(f)
      if (d) samples.push(d)
    }
    const ctx = cv.getContext('2d')
    if (!ctx) return
    fitCanvas(cv, ctx)
    ctx.clearRect(0, 0, CW, CH)
    ctx.fillStyle = 'rgba(0,0,0,0.25)'
    ctx.fillRect(0, 0, CW, CH)
    if (samples.length) {
      const grid = accumulate(samples, samples.length - 1, {
        team,
        windowFrames: samples.length,
        // The half-life is stated in real frames; the samples are every
        // HEAT_STEP-th frame, so it scales down with the stride.
        halfLifeFrames: HALF_LIFE_FRAMES / HEAT_STEP,
      })
      const img = renderGrid(grid, hexRgb(color))
      if (img) {
        ctx.imageSmoothingEnabled = true
        ctx.drawImage(img, 0, 0, CW, CH)
      }
    }
    drawPitchLines(ctx)
  }, [cache, frame, team, color, kickoffFrame, resizeEpoch])

  return (
    <canvas ref={ref} width={CW} height={CH} className="gms-team-panel__canvas"
            aria-label="Team heat map, last minute of play" />
  )
}

/**
 * Window-scoped heat: uniform accumulation (no recency decay) over an
 * explicit frame range — "where the team was during THIS window", the
 * segment workbench's unit of work. frameEnd clamped to frameStart so a
 * building view (start -> playhead) renders empty rather than backwards.
 */
export function WindowHeatPanel({ cache, frameStart, frameEnd, team, color, ariaLabel }: {
  cache: TrackingDataCache | null
  frameStart: number
  frameEnd: number
  team: number
  color: string
  ariaLabel: string
}) {
  const ref = useRef<HTMLCanvasElement>(null)
  const resizeEpoch = useCanvasResizeEpoch(ref)

  useEffect(() => {
    const cv = ref.current
    if (!cv) return
    const ctx = cv.getContext('2d')
    if (!ctx) return
    fitCanvas(cv, ctx)
    ctx.clearRect(0, 0, CW, CH)
    ctx.fillStyle = 'rgba(0,0,0,0.25)'
    ctx.fillRect(0, 0, CW, CH)
    const end = Math.max(frameStart, frameEnd)
    const samples: TrackingFrame[] = []
    if (cache) {
      for (let f = frameStart; f <= end; f += HEAT_STEP) {
        const d = cache.getFrameData(f)
        if (d) samples.push(d)
      }
    }
    if (samples.length) {
      // halfLifeFrames: 0 disables decay — every frame in the window
      // counts equally, unlike the trailing live panel.
      const grid = accumulate(samples, samples.length - 1, {
        team, windowFrames: samples.length, halfLifeFrames: 0,
      })
      const img = renderGrid(grid, hexRgb(color))
      if (img) {
        ctx.imageSmoothingEnabled = true
        ctx.drawImage(img, 0, 0, CW, CH)
      }
    }
    drawPitchLines(ctx)
  }, [cache, frameStart, frameEnd, team, color, resizeEpoch])

  return (
    <canvas ref={ref} width={CW} height={CH} className="gms-team-panel__canvas"
            aria-label={ariaLabel} />
  )
}

export function TeamPassPanel({ passes, frame, team, color }: {
  passes: CompletedPass[]
  frame: number
  team: number
  color: string
}) {
  const recent = passes
    .filter((p) => p.team === team && p.frame <= frame)
    .slice(-PASS_TRAIL)
  const S = 4
  const px = (x: number) => (x + 52.5) * S
  const py = (y: number) => (34 - y) * S
  return (
    <div className="gms-team-panel__passwrap">
      <svg viewBox={`0 0 ${105 * S} ${68 * S}`} role="img"
           aria-label="Recent passes with direction">
        <rect x={0} y={0} width={105 * S} height={68 * S}
              fill="rgba(0,0,0,0.25)" stroke="rgba(255,255,255,0.35)" />
        <line x1={52.5 * S} x2={52.5 * S} y1={0} y2={68 * S}
              stroke="rgba(255,255,255,0.35)" />
        {recent.map((p, i) => {
          const t = recent.length > 1 ? i / (recent.length - 1) : 1
          const o = 0.25 + 0.7 * t
          const x1 = px(p.fromX), y1 = py(p.fromY)
          const x2 = px(p.toX), y2 = py(p.toY)
          const ang = Math.atan2(y2 - y1, x2 - x1)
          const ah = 6 + 3 * t
          return (
            <g key={`${p.frame}-${i}`} opacity={o}>
              <line x1={x1} y1={y1} x2={x2} y2={y2}
                    stroke={color} strokeWidth={1 + 1.6 * t} />
              <polygon
                fill={color}
                points={[
                  `${x2},${y2}`,
                  `${x2 - ah * Math.cos(ang - 0.42)},${y2 - ah * Math.sin(ang - 0.42)}`,
                  `${x2 - ah * Math.cos(ang + 0.42)},${y2 - ah * Math.sin(ang + 0.42)}`,
                ].join(' ')} />
            </g>
          )
        })}
      </svg>
      <p className="gms-team-panel__count">
        {recent.length
          ? `last ${recent.length} passes`
          : 'no passes yet'}
      </p>
    </div>
  )
}

/**
 * The aggregated network with NUMBERS: jerseys at their mean on-ball
 * positions, edges weighted by how often the pair combined — the season-
 * ticket view of who plays with whom, beside the recency trail.
 */
export function TeamPassNetworkPanel({ passes, frame, team, color,
                                        flipX = false, status }: {
  passes: CompletedPass[]
  /** How the game's pass data loaded: 'none' and 'failed' are said, not
   *  shown as "no passes yet" for the whole match (PRI-1826). */
  status?: PassPotentialStatus
  frame: number
  team: number
  color: string
  /** Mirror left-right — the Workshop dashboard lets the two teams face
   *  opposing directions and swap, to match the video. Coordinates
   *  mirror; jersey numbers stay upright. */
  flipX?: boolean
}) {
  const net = aggregateNetwork(passes, team, frame)
  const S = 4
  const px = (x: number) => (flipX ? 52.5 - x : x + 52.5) * S
  const py = (y: number) => (34 - y) * S
  const maxN = Math.max(1, ...net.edges.map((e) => e.n))
  const maxT = Math.max(1, ...net.nodes.map((n) => n.touches))
  const at = new Map(net.nodes.map((n) => [n.jersey, n]))
  return (
    <div className="gms-team-panel__passwrap">
      <svg viewBox={`0 0 ${105 * S} ${68 * S}`} role="img"
           aria-label="Pass network with pass counts">
        <rect x={0} y={0} width={105 * S} height={68 * S}
              fill="rgba(0,0,0,0.25)" stroke="rgba(255,255,255,0.35)" />
        <line x1={52.5 * S} x2={52.5 * S} y1={0} y2={68 * S}
              stroke="rgba(255,255,255,0.35)" />
        {net.edges.map((e) => {
          const a = at.get(e.from), b = at.get(e.to)
          if (!a || !b) return null
          return (
            <line key={`${e.from}>${e.to}`}
                  x1={px(a.x)} y1={py(a.y)} x2={px(b.x)} y2={py(b.y)}
                  stroke={color} strokeOpacity={0.28 + 0.5 * (e.n / maxN)}
                  strokeWidth={0.8 + 4 * (e.n / maxN)} />
          )
        })}
        {net.nodes.map((n) => (
          <g key={n.jersey}>
            <circle cx={px(n.x)} cy={py(n.y)}
                    r={7 + 5 * (n.touches / maxT)}
                    fill={color} stroke="rgba(0,0,0,0.4)" />
            <text x={px(n.x)} y={py(n.y) + 3} textAnchor="middle"
                  className="gms-team-panel__jersey">{n.jersey}</text>
          </g>
        ))}
      </svg>
      <p className="gms-team-panel__count">
        {net.total
          ? `${net.total} passes among ${net.nodes.length} players`
          : status === 'none' ? 'No pass data for this game'
            : status === 'failed' ? 'Could not load pass data'
              : status === 'loading' ? 'Loading pass data…' : 'no passes yet'}
      </p>
    </div>
  )
}

/**
 * Passes into the box, so far: the attacking half only, the team's goal
 * end drawn where the video has it this half (attacksLeft = the right-hand
 * column), every half's entries on that one end.
 */
export function TeamBoxPassesPanel({ passes, frame, team, color, attacksLeft, status }: {
  passes: CompletedPass[]
  frame: number
  team: number
  color: string
  attacksLeft: boolean
  status?: PassPotentialStatus
}) {
  const entries = passesIntoBox(passes, team, frame)
  const S = 4
  const W = 52.5 * S, H = 68 * S
  // Oriented x runs 0 (halfway) -> 52.5 (goal line). Attacking left is a
  // half-turn of the pitch, so y flips with x.
  const px = (x: number) => (attacksLeft ? 52.5 - x : x) * S
  const py = (y: number) => (attacksLeft ? 34 + y : 34 - y) * S
  const boxX = attacksLeft ? 0 : W - BOX_DEPTH_M * S
  // A dark kit vanishes on the dark card: halo every mark in the dot
  // ring's contrast color (white for dark kits, dark for light ones).
  const halo = dotContrast(color).ring
  return (
    <div className="gms-team-panel__passwrap">
      <svg viewBox={`0 0 ${W} ${H}`} role="img"
           aria-label="Passes into the box, attacking half">
        <rect x={0} y={0} width={W} height={H}
              fill="rgba(0,0,0,0.25)" stroke="rgba(255,255,255,0.35)" />
        <rect x={boxX} y={(34 - BOX_HALF_WIDTH_M) * S}
              width={BOX_DEPTH_M * S} height={2 * BOX_HALF_WIDTH_M * S}
              fill="none" stroke="rgba(255,255,255,0.35)" />
        {entries.map((p, i) => {
          // Played from their own half: start the line at halfway, where
          // the panel's pitch begins, rather than off its edge.
          const t = p.fromX < 0 ? -p.fromX / (p.toX - p.fromX) : 0
          const x1 = px(p.fromX + t * (p.toX - p.fromX))
          const y1 = py(p.fromY + t * (p.toY - p.fromY))
          const x2 = px(p.toX), y2 = py(p.toY)
          return (
            <g key={`${p.frame}-${i}`}>
              <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={halo}
                    strokeOpacity={0.35} strokeWidth={3.2} />
              <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={color}
                    strokeOpacity={0.85} strokeWidth={1.6} />
              <circle cx={x2} cy={y2} r={3.5} fill={color} stroke={halo} />
            </g>
          )
        })}
      </svg>
      <p className="gms-team-panel__count">
        {entries.length
          ? `${entries.length} pass${entries.length === 1 ? '' : 'es'} into the box`
          : status === 'none' ? 'No pass data for this game'
            : status === 'failed' ? 'Could not load pass data'
              : status === 'loading' ? 'Loading pass data…' : 'none yet'}
      </p>
    </div>
  )
}

const ZONE_LABEL: Record<typeof CHAIN_ZONES[number], string> = {
  'defensive': 'Own third',
  'mid-def': 'Own half',
  'mid-att': 'Their half',
  'attacking': 'Final third',
}

/**
 * Build-up patterns, so far: one row per zone a possession began in, the
 * bar split by where those possessions ended — darker reaches further.
 */
export function BuildUpPanel({ chains, frame, team, color }: {
  chains: Chain[] | null
  frame: number
  team: number
  color: string
}) {
  if (!chains) return <p className="gms-team-panel__count">Loading possessions…</p>
  if (!chains.length) return <p className="gms-team-panel__count">No possession data for this game</p>
  const rows = buildUpProfile(chains, team, frame)
  const maxN = Math.max(1, ...rows.map((r) => r.n))
  const edge = `inset 0 0 0 1px ${dotContrast(color).ring}`
  return (
    <div className="gms-buildup" role="table" aria-label="Build-up by starting zone">
      {rows.map((r) => (
        <div key={r.start} className="gms-buildup__row" role="row"
             title={CHAIN_ZONES.map((z, i) => `${ZONE_LABEL[z]}: ${r.ended[i]}`).join(' · ')}>
          <span className="gms-buildup__label" role="rowheader">{ZONE_LABEL[r.start]}</span>
          <span className="gms-buildup__bar" role="cell">
            {r.ended.map((n, i) => n > 0 && (
              <span key={i} style={{
                width: `${(n / maxN) * 100}%`, background: color, boxShadow: edge,
                opacity: 0.3 + 0.7 * (i / (CHAIN_ZONES.length - 1)),
              }} />
            ))}
          </span>
          <span className="gms-buildup__n" role="cell">
            {r.n}{r.boxEntries > 0 && <small> · {r.boxEntries} box</small>}
          </span>
        </div>
      ))}
      <p className="gms-team-panel__count">
        possessions by where they began; shade = where they ended
      </p>
    </div>
  )
}

/** How many sequences the card lists; the rest are in the Possessions rail. */
const SEQ_SHOWN = 5

/**
 * Attacking sequences, so far: possessions that reached the final third
 * or entered the box, newest first, each one a seek to its start.
 */
export function AttackingSequencesPanel({ chains, frame, team, fps, onSeekFrame }: {
  chains: Chain[] | null
  frame: number
  team: number
  fps: number
  onSeekFrame: (frame: number) => void
}) {
  if (!chains) return <p className="gms-team-panel__count">Loading possessions…</p>
  if (!chains.length) return <p className="gms-team-panel__count">No possession data for this game</p>
  const seqs = attackingSequences(chains, team, frame)
  const box = seqs.filter((c) => c.box_entry).length
  // a withheld xG (null) is not 0: no total unless every sequence has one
  const xg = seqs.every((c) => c.chain_xg != null)
    ? seqs.reduce((t, c) => t + (c.chain_xg ?? 0), 0) : null
  return (
    <div className="gms-seq">
      <p className="gms-team-panel__count">
        {seqs.length
          ? `${seqs.length} sequence${seqs.length === 1 ? '' : 's'} · ${box} box entr${box === 1 ? 'y' : 'ies'}`
            + (xg != null && xg >= 0.01 ? ` · ${xg.toFixed(2)} xG` : '')
          : 'none yet'}
      </p>
      <ol className="gms-seq__list">
        {seqs.slice(0, SEQ_SHOWN).map((c) => (
          <li key={c.chain_id}>
            <button type="button" className="gms-seq__row"
                    aria-current={frame >= c.start_frame && frame <= c.end_frame ? 'true' : undefined}
                    onClick={() => onSeekFrame(c.start_frame)}>
              {describeChain(c, fps)}
            </button>
          </li>
        ))}
      </ol>
    </div>
  )
}

/** Pitch half-extents in cm — tracking coordinates are cm from the centre
 *  spot, y positive toward the top touchline (same convention heatMap.ts
 *  bins with). */
const HALF_LEN_CM = 105 * 100 / 2
const HALF_WID_CM = 68 * 100 / 2

/**
 * The plain live pitch: every player as a dot at THIS frame, both teams,
 * plus the ball — positions without any control surface, beside the
 * spatial-control pitch so the two can be read against each other.
 */
// LiveFrameRef and useLiveFrame moved to shapePanels.tsx (2026-09-21) so
// the shape/ball panels can glide without an import cycle; re-exported
// below for this module's long-standing importers.

/** Unmounted since 2026-09-21 — the ball-heat trial took its slot in the
 *  team-split view. If the trial has not been reversed by 2026-10-05,
 *  delete this panel (same clock as TeamPassPanel). */
export function LivePlayersPanel({ cache, frameRef, fps, team0Color, team1Color }: {
  cache: TrackingDataCache | null
  frameRef: LiveFrameRef
  fps: number
  team0Color: string
  team1Color: string
}) {
  const frame = useLiveFrame(frameRef, fps)
  const d = cache?.getFrameData(frame) ?? null
  const S = 6
  const px = (x: number) => ((x + HALF_LEN_CM) / (2 * HALF_LEN_CM)) * 105 * S
  const py = (y: number) => ((HALF_WID_CM - y) / (2 * HALF_WID_CM)) * 68 * S
  return (
    <div className="gms-team-panel__passwrap">
      <svg viewBox={`0 0 ${105 * S} ${68 * S}`} role="img"
           aria-label="Player positions at the playhead">
        <rect x={0} y={0} width={105 * S} height={68 * S}
              fill="rgba(0,0,0,0.25)" stroke="rgba(255,255,255,0.35)" />
        <line x1={52.5 * S} x2={52.5 * S} y1={0} y2={68 * S}
              stroke="rgba(255,255,255,0.35)" />
        <circle cx={52.5 * S} cy={34 * S} r={9.15 * S} fill="none"
                stroke="rgba(255,255,255,0.35)" />
        {d?.players.map((p, i) => (
          <g key={i}>
            <circle cx={px(p.x)} cy={py(p.y)} r={8}
                    fill={p.team === 0 ? team0Color : team1Color}
                    stroke="rgba(0,0,0,0.4)" />
            <text x={px(p.x)} y={py(p.y) + 3} textAnchor="middle"
                  className="gms-team-panel__jersey">{p.jersey}</text>
          </g>
        ))}
        {d?.ball && (
          <circle cx={px(d.ball.x)} cy={py(d.ball.y)} r={4.5}
                  fill="#fff" stroke="rgba(0,0,0,0.5)" />
        )}
        {!d && (
          <text x={52.5 * S} y={34 * S} textAnchor="middle"
                className="gms-team-panel__jersey">loading tracking&hellip;</text>
        )}
      </svg>
    </div>
  )
}

/**
 * Pitch control, war view: the SAME model and look as the regular view's
 * spatial-control layer — computeControlGrid (the 1:1 port of the Python
 * KNN pipeline) colored with the seismic ramp (team color → white → team
 * color, gamma contrast, blur, alpha all matching SpatialControlManager's
 * config) — with the players and ball on top. Deliberately its own panel
 * rather than the spatial canvas: it shares the Players-Live skeleton so
 * the two pitches are the same size by construction, and one grid per
 * frame costs ~0.2 ms, no window machinery. The poles are the page's team
 * colors, whose defaults ARE the regular view's blue/red tokens, so out of
 * the box the two surfaces are identical.
 */
const CTRL_GAMMA = 0.9 // SpatialControlManager.config.gamma
const CTRL_BLUR_PX = 6 // .blurRadius
const CTRL_ALPHA = 0.85 // .alpha
const DEEP_T0 = 0.35 // team0's wider deep-color center (blueDeepThreshold)
const DEEP_T1 = 0.5 // team1's (redDeepThreshold)
const WHITE: [number, number, number] = [255, 255, 255]

function hexRgb(hex: string): [number, number, number] {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim())
  const v = m ? parseInt(m[1]!, 16) : 0x888888
  return [(v >> 16) & 255, (v >> 8) & 255, v & 255]
}

function mix(a: [number, number, number], b: [number, number, number], t: number): [number, number, number] {
  return [
    Math.round(a[0] + (b[0] - a[0]) * t),
    Math.round(a[1] + (b[1] - a[1]) * t),
    Math.round(a[2] + (b[2] - a[2]) * t),
  ]
}

/**
 * SpatialControlManager._getSeismicColor, restated without its sign flip:
 * computeControlGrid already puts the LOWER team id (team 0) positive, so
 * positive = team0's color, negative = team1's. Deep colors are the team
 * color darkened — the tokens' own deep variants sit near 0.5x, and
 * deriving keeps the ramp coherent when a user swaps team colors.
 */
function seismicColor(
  v: number,
  c0: [number, number, number], d0: [number, number, number],
  c1: [number, number, number], d1: [number, number, number],
): [number, number, number] {
  const clamped = Math.max(-1, Math.min(1, v))
  const g = Math.sign(clamped) * Math.pow(Math.abs(clamped), CTRL_GAMMA)
  if (g >= DEEP_T0) return mix(d0, c0, (1 - g) / (1 - DEEP_T0))
  if (g >= 0) return mix(c0, WHITE, (DEEP_T0 - g) / DEEP_T0)
  const w = -g
  if (w < DEEP_T1) return mix(WHITE, c1, w / DEEP_T1)
  return mix(c1, d1, (w - DEEP_T1) / (1 - DEEP_T1))
}

export function PitchControlPanel({ cache, frameRef, fps, team0Color, team1Color }: {
  cache: TrackingDataCache | null
  frameRef: LiveFrameRef
  fps: number
  team0Color: string
  team1Color: string
}) {
  const frame = useLiveFrame(frameRef, fps)
  const ref = useRef<HTMLCanvasElement>(null)
  const resizeEpoch = useCanvasResizeEpoch(ref)

  useEffect(() => {
    const cv = ref.current
    if (!cv) return
    const ctx = cv.getContext('2d')
    if (!ctx) return
    const scale = fitCanvas(cv, ctx)
    ctx.clearRect(0, 0, CW, CH)
    ctx.fillStyle = 'rgba(0,0,0,0.25)'
    ctx.fillRect(0, 0, CW, CH)
    const d = cache?.getFrameData(frame) ?? null
    if (d) {
      const c0 = hexRgb(team0Color)
      const c1 = hexRgb(team1Color)
      const d0 = mix(c0, [0, 0, 0], 0.5)
      const d1 = mix(c1, [0, 0, 0], 0.5)
      // Null when a degraded frame can't field two identified teams — the
      // pipeline (and the regular view) skip the same frames.
      const computed = computeControlGrid(d.players)
      if (computed) {
        const img = new ImageData(GRID_X_CELLS, GRID_Y_CELLS)
        for (let row = 0; row < GRID_Y_CELLS; row++) {
          for (let col = 0; col < GRID_X_CELLS; col++) {
            // Same index/flip as SpatialControlManager.draw: grid y runs
            // up-positive, image rows run down.
            const gridIdx = col * GRID_Y_CELLS + (GRID_Y_CELLS - 1 - row)
            const [r, g, b] = seismicColor(computed.grid[gridIdx] ?? 0, c0, d0, c1, d1)
            const idx = (row * GRID_X_CELLS + col) * 4
            img.data[idx] = r
            img.data[idx + 1] = g
            img.data[idx + 2] = b
            img.data[idx + 3] = 255
          }
        }
        const off = document.createElement('canvas')
        off.width = GRID_X_CELLS
        off.height = GRID_Y_CELLS
        off.getContext('2d')?.putImageData(img, 0, 0)
        ctx.save()
        ctx.imageSmoothingEnabled = true
        ctx.imageSmoothingQuality = 'high'
        ctx.globalAlpha = CTRL_ALPHA
        // ctx.filter blur works in device px, unaffected by the transform —
        // scale it so the surface softness matches the regular view at any size.
        ctx.filter = `blur(${CTRL_BLUR_PX * scale}px)`
        ctx.drawImage(off, 0, 0, CW, CH)
        ctx.restore()
      }
      for (const pl of d.players) {
        const x = ((pl.x + HALF_LEN_CM) / (2 * HALF_LEN_CM)) * CW
        const y = ((HALF_WID_CM - pl.y) / (2 * HALF_WID_CM)) * CH
        ctx.beginPath()
        ctx.arc(x, y, 7, 0, Math.PI * 2)
        ctx.fillStyle = pl.team === 0 ? team0Color : team1Color
        ctx.fill()
        ctx.strokeStyle = 'rgba(0,0,0,0.45)'
        ctx.stroke()
        ctx.fillStyle = '#fff'
        ctx.font = '700 8px sans-serif'
        ctx.textAlign = 'center'
        ctx.fillText(String(pl.jersey), x, y + 3)
      }
      if (d.ball) {
        const x = ((d.ball.x + HALF_LEN_CM) / (2 * HALF_LEN_CM)) * CW
        const y = ((HALF_WID_CM - d.ball.y) / (2 * HALF_WID_CM)) * CH
        ctx.beginPath()
        ctx.arc(x, y, 4, 0, Math.PI * 2)
        ctx.fillStyle = '#fff'
        ctx.fill()
        ctx.strokeStyle = 'rgba(0,0,0,0.5)'
        ctx.stroke()
      }
    }
    drawPitchLines(ctx)
  }, [cache, frame, team0Color, team1Color, resizeEpoch])

  return (
    <canvas ref={ref} width={CW} height={CH} className="gms-team-panel__canvas"
            aria-label="Pitch control surface at the playhead" />
  )
}

/** The Field use card's legend. Rendered as a REAL tooltip, not the
 *  browser's `title` balloon — that one hides behind a ~1 second hover
 *  delay and a "?" help cursor, and read as broken (Ben, 2026-09-21). */
const FIELD_USE_LEGEND: [string, string][] = [
  ['Shading', 'share of outfield time spent in each zone over the selected '
    + 'window \u2014 brighter means more time; zones holding 10%+ print the '
    + 'number.'],
  ['White dot', 'the team\u2019s average outfield position for the window.'],
  ['Dashed ellipse', 'one standard deviation of spread around that average '
    + '\u2014 tight means compact, stretched means strung out.'],
  ['Fine print', 'the keeper is excluded, and windows that span half-time '
    + 'are re-oriented to the current half\u2019s direction of play.'],
]

/** The (i) and its tooltip: instant on hover, keyboard-reachable (focus
 *  shows it too), anchored away from the screen edge per column side. */
function FieldUseInfo({ side }: { side: 'left' | 'right' }) {
  const tipId = useId()
  return (
    <span className={`gms-info gms-info--${side}`} tabIndex={0}
          aria-describedby={tipId}>
      <i className="ph ph-info" aria-hidden="true" />
      <span id={tipId} role="tooltip" className="gms-info__tip">
        {FIELD_USE_LEGEND.map(([term, desc]) => (
          <span key={term} className="gms-info__row">
            <strong>{term}:</strong> {desc}
          </span>
        ))}
      </span>
    </span>
  )
}

/** GET /api/v1/game-match-summary/ppda/<code>: each team's PPDA, home and
 *  away. Only a stack-served game has one (source "priori-stack-v1"); a
 *  legacy game answers source "legacy" with nulls, because the legacy
 *  parquet's PPDA counts on a different scale and is not this measure.
 *  null where a stack build has no input for a team. */
export type TeamPpda = {
  ppda: { home: number | null; away: number | null }
  source: string | null
  definition: string | null
}

/** Each team's PPDA for the match page: null until it loads, when the
 *  fetch fails, or for anyone but Priori staff. Staff only for now (Ben,
 *  2026-10-25) — the route refuses everyone else, so a club session never
 *  asks. A new game starts from null so a failed fetch cannot leave the
 *  previous game's values beside the new team names. */
export function useTeamPpda(gameCode: string, staff: boolean): TeamPpda | null {
  const [teamPpda, setTeamPpda] = useState<TeamPpda | null>(null)
  useEffect(() => {
    setTeamPpda(null)
    if (!staff) return
    let cancelled = false
    ;(async () => {
      try {
        const res = await fetch(
          `/api/v1/game-match-summary/ppda/${pathSeg(gameCode)}`,
          { credentials: 'same-origin' },
        )
        const data = (await res.json()) as TeamPpda & { success?: boolean }
        if (!cancelled && data.success && data.ppda) setTeamPpda(data)
      } catch {
        // A failed fetch leaves teamPpda null: no PPDA stat, not a broken page.
      }
    })()
    return () => {
      cancelled = true
    }
  }, [gameCode, staff])
  return teamPpda
}

/** The only source whose PPDA the match page draws. */
export const PPDA_SOURCE = 'priori-stack-v1'

/** One team's PPDA from the response: `team` is a tracking index, and
 *  which index is home is the per-feed trackingHomeIndex, never assumed.
 *  undefined — draw no stat at all — while loading, on a failed fetch,
 *  and for any game not computed from the stack; null when a stack game
 *  has no value for the team (drawn as a dash). */
export function ppdaForTeam(
  data: TeamPpda | null | undefined, team: number, trackingHomeIndex: number,
): number | null | undefined {
  if (!data || data.source !== PPDA_SOURCE) return undefined
  return team === trackingHomeIndex ? data.ppda.home : data.ppda.away
}

/** "PPDA 8.40"; "PPDA —" when a stack game has no value (null: a missing
 *  value is never drawn as 0, which would read as the hardest press there
 *  is); nothing at all when value is undefined (ppdaForTeam: loading,
 *  failed, or a game not computed from the stack). */
export function TeamPpdaStat({ side, value, definition }: {
  side: 'left' | 'right'
  value: number | null | undefined
  definition?: string | null
}) {
  const tipId = useId()
  if (value === undefined) return null
  const shown = typeof value === 'number' && Number.isFinite(value)
    ? value.toFixed(2) : '\u2014'
  return (
    <span className="gms-team-col__ppda">
      <span className="gms-team-col__ppda-label">PPDA</span>
      <span className="gms-team-col__ppda-value" data-testid="team-ppda">{shown}</span>
      <span className={`gms-info gms-info--${side}`} tabIndex={0}
            aria-describedby={tipId}>
        <i className="ph ph-info" aria-hidden="true" />
        <span id={tipId} role="tooltip" className="gms-info__tip">
          <span className="gms-info__row">
            <strong>Passes per defensive action</strong>
            {definition ? <>: {definition}</> : null} Lower is a more
            intense press.
          </span>
          <a className="gms-info__row" href="/glossary/#passes-per-defensive-action">
            See in glossary
          </a>
        </span>
      </span>
    </span>
  )
}

export function TeamColumn({ side, label, color, oppColor, team, cache, frame, frameRef, passes, passStatus, chains, onSeekFrame, kickoffFrame, fps, homeStartDirection, homeIndex, ppda, ppdaDefinition }: {
  side: 'left' | 'right'
  label: string
  color: string
  /** The OPPONENT's color — the shape card draws their offside line in it. */
  oppColor: string
  team: number
  cache: TrackingDataCache | null
  /** The 1 Hz tick — the windowed heat and network panels key on it. */
  frame: number
  /** The live playhead — the shape card glides on it. */
  frameRef: LiveFrameRef
  passes: CompletedPass[]
  passStatus?: PassPotentialStatus
  /** The game's possession chains — null while loading. */
  chains: Chain[] | null
  onSeekFrame: (frame: number) => void
  kickoffFrame: number
  fps: number
  /** Orients the shape card's offside line and the keeper exclusion;
   *  null blocks those readings rather than guessing (ledger 26). */
  homeStartDirection: string | null
  /** The tracking index of the home side (lib/homeIndex). */
  homeIndex: 0 | 1
  /** This team's PPDA (ppdaForTeam): undefined draws no stat (loading,
   *  failed, or not a stack game); null draws a dash. */
  ppda?: number | null
  ppdaDefinition?: string | null
}) {
  return (
    <aside className={`gms-team-col gms-team-col--${side}`}>
      <div className={`gms-team-col__netwrap gms-team-col__netwrap--${side}`}>
        <div className="gms-team-col__head">
          <span className="gms-team-col__dot" style={{ background: color }} />
          <strong>{label}</strong>
          <span className="gms-team-col__side">{side} goal</span>
        </div>
        <TeamPpdaStat side={side} value={ppda} definition={ppdaDefinition} />
        <p className="gms-team-panel__title">Pass network &middot; so far</p>
        <TeamPassNetworkPanel passes={passes} frame={frame} team={team} color={color}
                              status={passStatus} />
      </div>
      <div className={`gms-team-col__heatwrap gms-team-col__heatwrap--${side}`}>
        {/* Was raw touch-density heat ("dust" at one-minute windows) until
            2026-09-21 — zone occupancy speaks thirds and channels instead
            (Ben). TeamHeatPanel stays defined for the dashboard's card. */}
        {/* The info icon sits BESIDE the title, not inside it: the title's
            own opacity would dim any tooltip nested under it, since a
            child cannot exceed its parent's opacity. */}
        <div className="gms-team-panel__titlerow">
          <p className="gms-team-panel__title">Field use &middot; {label}</p>
          <FieldUseInfo side={side} />
        </div>
        <ZoneOccupancyPanel cache={cache} frame={frame} team={team === 0 ? 0 : 1}
                            color={color} kickoffFrame={kickoffFrame} fps={fps}
                            homeStartDirection={homeStartDirection}
                            homeIndex={homeIndex} />
      </div>
      <div className={`gms-team-col__passwrap gms-team-col__passwrap--${side}`}>
        {/* Was "Passes · {label}" (TeamPassPanel) until 2026-09-21 — the
            shape card is on trial in its slot (Ben). TeamPassPanel stays
            defined above in case the trial reverts; if the trial has not
            been reversed by 2026-10-05, delete the panel. */}
        <p className="gms-team-panel__title">Shape &amp; line &middot; {label}</p>
        <TeamShapePanel cache={cache} frameRef={frameRef} fps={fps}
                        team={team === 0 ? 0 : 1} color={color} oppColor={oppColor}
                        teamLabel={label}
                        homeStartDirection={homeStartDirection}
                        homeIndex={homeIndex} />
      </div>
      <div className={`gms-team-col__boxwrap gms-team-col__boxwrap--${side}`}>
        <p className="gms-team-panel__title">Passes into the box &middot; so far</p>
        {/* The left column's team defends the left goal, so attacks right. */}
        <TeamBoxPassesPanel passes={passes} frame={frame} team={team} color={color}
                            attacksLeft={side === 'right'} status={passStatus} />
      </div>
      <div className={`gms-team-col__buildwrap gms-team-col__buildwrap--${side}`}>
        <p className="gms-team-panel__title">Build-up &middot; {label}</p>
        <BuildUpPanel chains={chains} frame={frame} team={team} color={color} />
      </div>
      <div className={`gms-team-col__seqwrap gms-team-col__seqwrap--${side}`}>
        <p className="gms-team-panel__title">Attacking sequences &middot; {label}</p>
        <AttackingSequencesPanel chains={chains} frame={frame} team={team} fps={fps}
                                 onSeekFrame={onSeekFrame} />
      </div>
    </aside>
  )
}
